import { useCallback, useRef, useState } from 'react';

import type { Editor } from '@tiptap/core';

/** One shared "active editor" slot for a whole block editor — InputBlockFrame renders a single
 * toolbar box (anchored to the top of whichever of editingSlot/FeedbackPanel is currently
 * showing) that always targets whichever RichTextEditor last registered here. A block wires its
 * own question editor, option/item rows, and FeedbackCard content editors into this same object
 * (via each RichTextEditor's onEditorReady/onEditorFocus, or FeedbackCard's activeEditorRef/
 * setActiveEditor props) so all of them share the one toolbar instead of each showing its own. */
export interface SharedEditorToolbar {
  activeEditor: Editor | null;
  setActiveEditor: (editor: Editor | null) => void;
  activeEditorRef: { current: Editor | null };
  /**
   * Releases the toolbar, for a field that is NOT rich text.
   *
   * Focusing a plain numeric box (a threshold, a tolerance, a tick interval) leaves the toolbar
   * bound to whichever rich-text editor was focused last, so pressing Bold applies it to a
   * different field elsewhere on screen — a misfire at a stale target, not merely a misleading
   * affordance. Clearing `activeEditor` also greys the toolbar out, since InputBlockFrame drives
   * `toolbarVisible` from it, so disconnecting is all a caller needs to do.
   *
   * Lives on the hook because ten blocks needed it and five had each written the same two lines
   * inline under their own name.
   */
  disconnect: () => void;
}

export function useSharedEditorToolbar(): SharedEditorToolbar {
  const [activeEditor, setActiveEditor] = useState<Editor | null>(null);
  const activeEditorRef = useRef<Editor | null>(null);
  const disconnect = useCallback(() => {
    activeEditorRef.current = null;
    setActiveEditor(null);
  }, []);
  return { activeEditor, setActiveEditor, activeEditorRef, disconnect };
}
